import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Layout from '../../components/Layout';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import { systemService } from '../../services/system.service';
import { OtpFallbackConfig } from '../../types/system';
import { getErrorMsg } from '../../utils/error';

const OtpFallbackPage = () => {
  const { t } = useTranslation('common');
  const [config, setConfig] = useState<OtpFallbackConfig>({ enabled: false, code: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    (async () => {
      try {
        setConfig(await systemService.getOtpFallbackConfig());
      } catch (e) {
        setError(getErrorMsg(e, 'Failed to load setting'));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleSave = async () => {
    // Checked here as well as in the API: the backend rejects a bad code with a
    // 422 the admin would have to decode, and an unusable code silently locks
    // out the users the fallback exists to rescue.
    if (!/^\d{6}$/.test(config.code)) {
      setError(t('otp_fallback_code_invalid'));
      return;
    }
    setSaving(true); setError(''); setSuccess('');
    try {
      setConfig(await systemService.setOtpFallbackConfig(config));
      setSuccess(t('saved'));
    } catch (e) {
      setError(getErrorMsg(e, 'Failed to save'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Layout title={t('otp_fallback_settings')}>
      <div className="space-y-6 max-w-2xl">
        {error && <div className="p-3 rounded-md bg-red-50 text-red-600 border border-red-100">{error}</div>}
        {success && <div className="p-3 rounded-md bg-green-50 text-green-600 border border-green-100">{success}</div>}

        <Card className="p-5 space-y-4">
          <div>
            <h2 className="text-base font-semibold">{t('otp_fallback_settings')}</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{t('otp_fallback_hint')}</p>
          </div>

          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              checked={config.enabled}
              disabled={loading}
              onChange={(e) => setConfig((c) => ({ ...c, enabled: e.target.checked }))}
            />
            <span className="text-sm">{t('otp_fallback_enabled')}</span>
          </label>

          <Input
            label={t('otp_fallback_code')}
            value={config.code}
            disabled={loading}
            inputMode="numeric"
            maxLength={6}
            placeholder="260888"
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setConfig((c) => ({ ...c, code: e.target.value.replace(/\D/g, '').slice(0, 6) }))
            }
          />

          <p className="text-xs text-amber-600 dark:text-amber-400">{t('otp_fallback_warning')}</p>

          <Button variant="primary" onClick={handleSave} disabled={loading || saving}>
            {saving ? t('saving') : t('save')}
          </Button>
        </Card>
      </div>
    </Layout>
  );
};

export default OtpFallbackPage;
